import React, { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Haptic feedback utility
 * Uses Vibration API for supported devices
 */
const haptics = {
  // Light tap - when reaching threshold
  light: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  },
  // Medium tap - when releasing to refresh
  medium: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(20);
    }
  },
  // Success pattern - when refresh completes
  success: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate([15, 50, 15]);
    }
  },
  // Double tap - subtle confirmation
  double: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate([10, 30, 10]);
    }
  }
};

/**
 * Pull-to-Refresh component for mobile
 * Wraps content and adds pull-to-refresh functionality with haptic feedback
 */
const PullToRefresh = ({ 
  onRefresh, 
  children, 
  threshold = 80,
  disabled = false 
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [hasReachedThreshold, setHasReachedThreshold] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    // Only activate if at top of scroll
    const container = containerRef.current;
    if (container && container.scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
      setHasReachedThreshold(false);
    }
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const distance = Math.max(0, currentY.current - startY.current);
    
    // Apply resistance for natural feel
    const resistedDistance = Math.min(distance * 0.5, threshold * 1.5);
    setPullDistance(resistedDistance);
    
    // Haptic feedback when reaching threshold
    if (resistedDistance >= threshold && !hasReachedThreshold) {
      setHasReachedThreshold(true);
      haptics.light();
    } else if (resistedDistance < threshold && hasReachedThreshold) {
      setHasReachedThreshold(false);
    }
    
    // Prevent default scroll when pulling
    if (distance > 10) {
      e.preventDefault();
    }
  }, [isPulling, disabled, isRefreshing, threshold, hasReachedThreshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    setHasReachedThreshold(false);
    
    if (pullDistance >= threshold && onRefresh) {
      // Haptic feedback on release
      haptics.medium();
      
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6); // Keep spinner visible
      
      try {
        await onRefresh();
        // Success haptic
        haptics.success();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
    
    startY.current = 0;
    currentY.current = 0;
  }, [isPulling, disabled, pullDistance, threshold, onRefresh]);

  const progress = Math.min(pullDistance / threshold, 1);
  const rotation = progress * 180;
  const scale = 0.5 + (progress * 0.5);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: isPulling ? 'none' : 'auto' }}
    >
      {/* Pull indicator */}
      <div 
        className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-10 transition-opacity duration-200"
        style={{ 
          top: pullDistance - 50,
          opacity: pullDistance > 10 ? 1 : 0,
        }}
      >
        <div 
          className={`
            flex items-center justify-center
            w-10 h-10 rounded-full
            bg-gray-900/90 border border-gray-700
            shadow-lg backdrop-blur-sm
            transition-all duration-200
            ${hasReachedThreshold && !isRefreshing ? 'border-yellow-500 shadow-yellow-500/20' : ''}
          `}
          style={{ 
            transform: `scale(${scale})`,
          }}
        >
          <RefreshCw 
            className={`w-5 h-5 ${hasReachedThreshold || isRefreshing ? 'text-yellow-400' : 'text-gray-400'} ${isRefreshing ? 'animate-spin' : ''}`}
            style={{ 
              transform: isRefreshing ? 'none' : `rotate(${rotation}deg)`,
              transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
            }}
          />
        </div>
      </div>

      {/* Pull text indicator */}
      {pullDistance > 20 && !isRefreshing && (
        <div 
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-10"
          style={{ top: pullDistance + 5 }}
        >
          <span className={`text-xs font-medium ${hasReachedThreshold ? 'text-yellow-400' : 'text-gray-400'}`}>
            {hasReachedThreshold ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      )}

      {/* Refreshing text */}
      {isRefreshing && (
        <div 
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-10"
          style={{ top: pullDistance + 5 }}
        >
          <span className="text-xs text-yellow-400 font-medium">
            Refreshing...
          </span>
        </div>
      )}

      {/* Content with pull offset */}
      <div 
        style={{ 
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
